import React from 'react';
import type { Specialty } from '@/lib/api/specialties';
import { INPUT_CLASS } from '@/components/forms/formStyles';
import { useSpecialtyMatches } from '@/lib/hooks/useSpecialtyMatches';

export interface SpecialtySearchProps {
  specialties: Specialty[];
  /** Text in the box */
  query: string;
  onQueryChange: (query: string) => void;
  /** A suggestion was picked (click, or Enter on the highlighted one) */
  onPick: (specialty: Specialty) => void;
  /** Enter with no suggestion highlighted — for boxes that accept free text */
  onEnterText?: (text: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Shown in the dropdown instead of suggestions, e.g. while loading or on error */
  statusMessage?: string;
}

/**
 * A text box that suggests catalogue specialties as you type. Keyboard: ↑/↓ to
 * move, Enter to pick, Esc to close.
 */
export function SpecialtySearch({
  specialties,
  query,
  onQueryChange,
  onPick,
  onEnterText,
  onBlur,
  placeholder = 'Type to search specialties',
  disabled,
  invalid,
  statusMessage,
}: SpecialtySearchProps) {
  const [open, setOpen] = React.useState(false);
  const [highlighted, setHighlighted] = React.useState(-1);
  const matches = useSpecialtyMatches(specialties, query);
  const listId = React.useId();
  const listRef = React.useRef<HTMLUListElement>(null);

  // Typing changes the matches, so start again from nothing highlighted.
  React.useEffect(() => setHighlighted(-1), [query]);

  React.useEffect(() => {
    if (highlighted < 0) return;
    listRef.current?.children[highlighted]?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const pick = (specialty: Specialty) => {
    onPick(specialty);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlighted((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && highlighted >= 0 && matches[highlighted]) {
        pick(matches[highlighted]);
      } else if (onEnterText && query.trim()) {
        onEnterText(query.trim());
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showList = open && !disabled;

  return (
    <div className="relative">
      <input
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-invalid={invalid || undefined}
        className={`${INPUT_CLASS} ${invalid ? 'ring-destructive' : ''}`}
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          onQueryChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          onBlur?.();
        }}
        onKeyDown={onKeyDown}
        disabled={disabled}
        maxLength={100}
        autoComplete="off"
      />
      {showList && (
        <ul
          id={listId}
          ref={listRef}
          role="listbox"
          className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-gray-200"
        >
          {statusMessage ? (
            <li className="px-3 py-1.5 text-gray-500">{statusMessage}</li>
          ) : matches.length === 0 ? (
            <li className="px-3 py-1.5 text-gray-500">
              {onEnterText ? 'No match — press Enter to add as typed' : 'No matching specialty'}
            </li>
          ) : (
            matches.map((specialty, i) => (
              <li
                key={specialty.code}
                role="option"
                aria-selected={i === highlighted}
                // mousedown, not click: picking must happen before the input's blur closes the list.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(specialty);
                }}
                onMouseEnter={() => setHighlighted(i)}
                className={`cursor-pointer px-3 py-1.5 ${i === highlighted ? 'bg-primary-100 text-pranik-brand' : 'text-gray-800'}`}
              >
                {specialty.display}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

export interface SpecialtyPickerProps {
  specialties: Specialty[];
  /** Selected catalogue code */
  value: string;
  onChange: (code: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  invalid?: boolean;
  statusMessage?: string;
}

/**
 * Picks exactly one catalogue specialty. The box shows the chosen label; typing
 * searches, and leaving without picking restores the chosen label.
 */
export function SpecialtyPicker({
  specialties,
  value,
  onChange,
  onBlur,
  disabled,
  invalid,
  statusMessage,
}: SpecialtyPickerProps) {
  const selectedLabel = React.useMemo(
    () => specialties.find((specialty) => specialty.code === value)?.display ?? '',
    [specialties, value]
  );
  const [query, setQuery] = React.useState(selectedLabel);

  // Follow the selection (and the catalogue arriving after it) when not typing.
  React.useEffect(() => setQuery(selectedLabel), [selectedLabel]);

  return (
    <SpecialtySearch
      specialties={specialties}
      query={query}
      onQueryChange={setQuery}
      onPick={(specialty) => {
        onChange(specialty.code);
        setQuery(specialty.display);
      }}
      onBlur={() => {
        setQuery(selectedLabel);
        onBlur?.();
      }}
      disabled={disabled}
      invalid={invalid}
      statusMessage={statusMessage}
    />
  );
}
